import { useCallback, useMemo, useState, useEffect } from 'react';
import {
  MutationTuple,
  MutationUpdaterFn,
  QueryResult,
  useMutation,
  useQuery,
  Reference,
  ApolloCache,
  useApolloClient,
  useLazyQuery,
  ApolloError
} from '@apollo/client';
import {
  BTag,
  IApiMp,
  IMatchingProfile,
  PREFIX_SEARCH_LIMIT,
  ContractValueRange,
  IKeyword,
  SearchType,
  FilterLogic,
  HighlightColor,
  IMpSnapshot,
  IMpSnapshotValues,
  INIT_SNAPSHOT,
  CodeFilter,
  MpCode,
  MpCodeTypes,
  isCpvCodesResponse,
  CpvCodesResponse,
  NutsCodesResponse,
  MpContractValue,
  MpNewKeyword,
  IMpValues
} from './types';
import {
  CREATE_MP,
  DELETE_MP,
  LOAD_ALL_MY_MP,
  LOAD_BTAGS,
  LOAD_MP,
  SEARCH_CPV_CODES,
  SEARCH_REGIONS,
  UPDATE_MP_BTAGS,
  UPDATE_MP_CPV_CODES,
  UPDATE_MP_NAME,
  UPDATE_MP_NUTS_CODES,
  UPDATE_MP_BUYERS,
  DELETE_MP_KEYWORD,
  UPDATE_MP_KEYWORD,
  CREATE_MULTIPLE_MP_KEYWORD,
  LOAD_ALL_MY_MP_IDS,
  LOAD_MP_KEYWORDS,
  UPDATE_MP_BUYER_TYPES,
  UPDATE_MP_CONTRACT_VALUE,
  GET_NUTS_FILTER,
  UPDATE_MP
} from './queries';
import { notification } from 'src/common';
import { isString, useQueryParams } from 'src/helpers';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OverviewQp } from '../procurements/types';
import { isEqual } from 'lodash';
import { MatchingProfile } from '.';
import {
  SupplierOrg,
  SupplierString,
  ApiTendersData,
  ApiTendersVars,
  TendersSearchParams
} from '../procurements/Tenders/types';
import { GET_TENDERS } from '../procurements/Tenders/queries';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import { BuyerType, JunctionLogic, ProfileType, SearchLogic } from '@tendium/prom-types';

export function clearTendersCacheOnMpUpdate(cache: ApolloCache<unknown>, mpId: string): void {
  const tendersQ: string[] = [];
  cache.modify({
    fields: {
      getTenders: (existing, { storeFieldName }) => {
        if (storeFieldName.includes(mpId)) {
          tendersQ.push(storeFieldName);
        }
        return existing;
      }
    }
  });
  !!tendersQ.length &&
    tendersQ.forEach(queryId => {
      cache.evict({
        fieldName: queryId
      });
    });
  cache.gc();
}

export function useLoadAllMps(skip?: boolean): Omit<QueryResult<{ getAllMyMatchingProfiles: IApiMp[] }>, 'data'> & {
  data?: IMatchingProfile[];
} {
  const query = useQuery<{ getAllMyMatchingProfiles: IApiMp[] }>(LOAD_ALL_MY_MP, {
    skip
  });
  return useMemo(
    () => ({
      ...query,
      data: query.data ? query.data.getAllMyMatchingProfiles.map(mp => new MatchingProfile(mp)) : undefined
    }),
    [query]
  );
}

export type MpShort = Pick<MatchingProfile, 'id' | 'name' | 'profileType'>;
interface LoadMpIdsResponse {
  getAllMyMatchingProfiles: MpShort[];
}
export function useLoadAllMpsIds(skip?: boolean, profileType?: ProfileType): QueryResult<LoadMpIdsResponse> {
  return useQuery<LoadMpIdsResponse>(LOAD_ALL_MY_MP_IDS, {
    variables: profileType ? { input: { profileType } } : undefined,
    skip
  });
}

/** @deprecated check just qp['matchingProfileId] */
export function useActiveMpIdState(): [string | undefined, (id: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: mpsData, loading } = useLoadAllMpsIds();
  const mps = mpsData?.getAllMyMatchingProfiles;
  const qpMpId = searchParams.get(OverviewQp.MatchingProfileId);
  const activeMpId = useMemo(() => {
    if (!mps || loading) {
      return;
    }

    if (isString(qpMpId) && mps.find(_ => _.id === qpMpId)) {
      return qpMpId;
    }

    if (mps.length) {
      return mps[0].id;
    }

    return undefined;
  }, [loading, mps, qpMpId]);
  const setActiveMpId = useCallback(
    (id: string) => {
      setSearchParams({
        [OverviewQp.MatchingProfileId]: id
      });
    },
    [setSearchParams]
  );

  return [activeMpId, setActiveMpId];
}

export function useLoadMp(
  mpId?: string,
  skip?: boolean
): Omit<QueryResult<{ getMatchingProfile: IApiMp }, { id?: string }>, 'data'> & { data?: IMatchingProfile } {
  const qp = useQueryParams();
  const activeMpId = useMemo(
    () => (isString(qp[TendersSearchParams.matchingProfileId]) ? qp[TendersSearchParams.matchingProfileId] : undefined),
    [qp]
  );
  const id = useMemo(() => mpId ?? activeMpId, [activeMpId, mpId]);
  const query = useQuery<{ getMatchingProfile: IApiMp }, { id?: string }>(LOAD_MP, {
    variables: { id },
    skip: !id || skip
  });
  return useMemo(
    () => ({
      ...query,
      data: query.data ? new MatchingProfile(query.data.getMatchingProfile) : undefined
    }),
    [query]
  );
}

interface ILoadMpKeywordsResponse {
  id: string;
  keywords: {
    value: string;
    id: string;
  }[];
  name: string;
}

export function useMpKeywords(
  mpId?: string,
  skip?: boolean
): QueryResult<{ getMatchingProfile: ILoadMpKeywordsResponse }, { id?: string }> {
  return useQuery<{ getMatchingProfile: ILoadMpKeywordsResponse }, { id?: string }>(LOAD_MP_KEYWORDS, {
    variables: { id: mpId },
    skip: !mpId || skip
  });
}

export function useLoadBTags(): QueryResult<{ getBTags: BTag[] }> {
  return useQuery<{ getBTags: BTag[] }>(LOAD_BTAGS);
}

export function useLoadCpvCodes(): QueryResult<{
  searchCpvCodes: MpCode[];
}> {
  return useQuery<CpvCodesResponse>(SEARCH_CPV_CODES, {
    variables: { input: { query: '' } }
  });
}

export function useLoadNutsCodes(): QueryResult<{
  searchNutCodes: MpCode[];
}> {
  return useQuery<NutsCodesResponse>(SEARCH_REGIONS, {
    variables: { input: { query: '' } }
  });
}
export function useLoadMpCodes<Param extends MpCodeTypes>(
  type: Param
): { data?: MpCode[]; loading?: boolean; error?: ApolloError } {
  const query = useQuery<Param extends 'cpvCodes' ? CpvCodesResponse : NutsCodesResponse>(
    type === 'cpvCodes' ? SEARCH_CPV_CODES : SEARCH_REGIONS,
    {
      variables: { input: { query: '' } }
    }
  );
  return useMemo(
    () => ({
      ...query,
      data: query.data
        ? isCpvCodesResponse(query.data)
          ? query.data.searchCpvCodes
          : query.data.searchNutCodes
        : undefined
    }),
    [query]
  );
}

export function useNutsFilter(skip?: boolean): QueryResult<{
  getNutsFilter: CodeFilter;
}> {
  return useQuery<{ getNutsFilter: CodeFilter }>(GET_NUTS_FILTER, {
    skip: !!skip
  });
}

export interface IUpdateMpResponse {
  __typename: 'Mutation';
  updateMatchingProfile: IApiMp & {
    __typename: 'MatchingProfile';
  };
}
export type UpdateMpVars = Omit<
  IMpValues,
  | 'keywords'
  | 'supplierOrgIds'
  | 'supplierSearchStrings'
  | 'contractValueMin'
  | 'contractValueMax'
  | 'contractValueCurrency'
> & {
  id: string;
  keywords?: MpNewKeyword[];
  supplierOrgIds?: SupplierOrg[];
  supplierSearchStrings?: SupplierString[];
  contractValueRange?: ContractValueRange;
};
export function useUpdateMp(): [(input: UpdateMpVars, onComplete?: () => void) => void, { loading: boolean }] {
  const { t } = useTranslation();
  const [updateMp, { loading }] = useMutation(UPDATE_MP);
  const updateMpFn = useCallback(
    (input: UpdateMpVars, onComplete?: () => void) => {
      const { id, ...values } = input;

      updateMp({
        variables: {
          id,
          ...values
        },
        update: getUpdateCacheOnUpdateMp(input)
      })
        .then(() => {
          onComplete && onComplete();
        })
        .catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
    },
    [t, updateMp]
  );
  return [updateMpFn, { loading }];
}

export function getUpdateCacheOnUpdateMp(input: UpdateMpVars): MutationUpdaterFn<IUpdateMpResponse> {
  const { id, ...values } = input;

  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const mpResponse = data.updateMatchingProfile;
    const mpRef = cache.identify({
      __typename: 'MatchingProfile',
      id
    });
    for (const field in values) {
      if (field !== undefined) {
        if (field === 'keywords') {
          cache.modify({
            id: mpRef,
            fields: {
              keywords(_, { toReference }) {
                return (
                  mpResponse[field]?.map(keyword => toReference({ __typename: 'Keyword', id: keyword.id })) ?? null
                );
              }
            }
          });
        } else if (field === 'supplierOrgIds' || field === 'supplierSearchStrings') {
          cache.modify({
            id,
            fields: {
              ['supplierSearchTerms'](_, { toReference }) {
                return mpResponse['supplierSearchTerms']?.map(term =>
                  toReference({ __typename: 'SupplierSearchTerm', id: term.id })
                );
              }
            }
          });
        } else if (field === 'contractValue') {
          cache.modify({
            id,
            fields: {
              ['contractValueMin']() {
                return mpResponse['contractValueMin'];
              },
              ['contractValueMax']() {
                return mpResponse['contractValueMax'];
              },
              ['contractValueCurrency']() {
                return mpResponse['contractValueCurrency'];
              }
            }
          });
        } else {
          cache.modify({
            id,
            fields: {
              [field]() {
                return mpResponse[field as keyof IApiMp];
              }
            }
          });
        }
      }
    }
    cache.gc();
  };
}
interface IKeywordResp extends IKeyword {
  __typename: 'Keyword';
}

export interface ICreateKeywordResponse {
  __typename: 'Mutation';
  createMultipleKeywords: IKeywordResp[];
}

export interface ICreateKeywordPayload {
  matchingProfileId: string;
  keywords: string[];
}

export function useCreateMPKeywords(): [
  (data: { matchingProfileId: string; keywords: string[] }) => void,
  { loading: boolean; error: ApolloError | undefined }
] {
  const { t } = useTranslation();

  const [createKeyword, { loading, error }] = useMutation<ICreateKeywordResponse, ICreateKeywordPayload>(
    CREATE_MULTIPLE_MP_KEYWORD
  );
  const createKeyWordFn = useCallback(
    (data: { matchingProfileId: string; keywords: string[] }) => {
      const { matchingProfileId, keywords } = data;

      createKeyword({
        variables: {
          matchingProfileId,
          keywords
        },
        update: getUpdateCacheOnCreateKeyword(matchingProfileId)
      }).catch(() => {
        notification.error({
          description: t('Common.unknownErrorDesc'),
          message: t('Common.unknownError')
        });
      });
    },
    [createKeyword, t]
  );
  return [createKeyWordFn, { loading, error }];
}

export function getUpdateCacheOnCreateKeyword(mpId: string): MutationUpdaterFn<ICreateKeywordResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const mpRef = cache.identify({
      __typename: 'MatchingProfile',
      id: mpId
    });

    const newKeywordRefs = data.createMultipleKeywords.map(keyword => {
      return {
        __ref: cache.identify({
          __typename: 'Keyword',
          id: keyword.id
        })
      };
    });
    cache.modify({
      id: mpRef,
      fields: {
        keywords(existingRefs: Reference[] = []) {
          return [...existingRefs, ...newKeywordRefs];
        }
      }
    });
  };
}

export interface IUpdateKeywordResponse {
  __typename: 'Mutation';
  updateKeyword: IKeyword & {
    __typename: 'Keyword';
  };
}

export interface IUpdateKeywordPayload {
  id: string;
  value?: string;
  searchType?: SearchType;
  searchLogic?: SearchLogic;
  filterLogic?: FilterLogic;
  highlightColor?: HighlightColor;
}
export function useUpdateMPKeyword(): [
  (keyword: IKeyword) => void,
  { loading: boolean; error: ApolloError | undefined }
] {
  const { t } = useTranslation();
  const [updateKeyword, { loading, error }] = useMutation<IUpdateKeywordResponse, IUpdateKeywordPayload>(
    UPDATE_MP_KEYWORD
  );
  const updateKeywordFn = useCallback(
    (keyword: IKeyword) => {
      updateKeyword({
        variables: keyword
      }).catch(() => {
        notification.error({
          description: t('Common.unknownErrorDesc'),
          message: t('Common.unknownError')
        });
      });
    },
    [updateKeyword, t]
  );
  return useMemo(() => [updateKeywordFn, { loading, error }], [error, loading, updateKeywordFn]);
}

export function useDeleteMPKeyword(): [
  (keywordId: string, mpId: string) => void,
  { loading: boolean; error: ApolloError | undefined }
] {
  const { t } = useTranslation();
  const [deleteKeyword, { loading, error }] = useMutation<
    { deleteKeyword: boolean; __typename: 'Mutation' },
    { id: string }
  >(DELETE_MP_KEYWORD);

  const deleteKeyWordFn = useCallback(
    (keywordId: string, mpId: string) => {
      deleteKeyword({
        variables: {
          id: keywordId
        },
        update: getUpdateCacheOnDeleteKeyword(mpId, keywordId),
        optimisticResponse: {
          __typename: 'Mutation',
          deleteKeyword: true
        }
      }).catch(() => {
        notification.error({
          description: t('Common.unknownErrorDesc'),
          message: t('Common.unknownError')
        });
      });
    },
    [deleteKeyword, t]
  );
  return [deleteKeyWordFn, { loading, error }];
}

export function getUpdateCacheOnDeleteKeyword(
  mpId: string,
  keywordId: string
): MutationUpdaterFn<{ deleteKeyword: boolean; __typename: 'Mutation' }> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const mpRef = cache.identify({
      __typename: 'MatchingProfile',
      id: mpId
    });

    cache.modify({
      id: mpRef,
      fields: {
        keywords(existingRefs: Reference[], { readField }) {
          return existingRefs.filter(ref => readField('id', ref) !== keywordId);
        }
      }
    });

    cache.evict({
      id: cache.identify({
        __typename: 'Keyword',
        id: keywordId
      })
    });
    cache.gc();
  };
}

export function useUpdateMpCompaniesIds(): [
  (companiesIds: string[], searchStrings: string[], onComplete?: () => void) => void,
  { loading: boolean; error: Error | undefined }
] {
  const { t } = useTranslation();

  const [activeMpId] = useActiveMpIdState();
  const { data: mp } = useLoadMp(activeMpId);

  const [updateMpCompaniesIds, { loading, error }] = useMutation(UPDATE_MP_BUYERS);
  const updateMpCompaniesIdsFn = useCallback(
    (companiesIds: string[], searchStrings: string[], onComplete?: () => void) => {
      mp &&
        activeMpId &&
        updateMpCompaniesIds({
          variables: {
            id: activeMpId,
            buyerOrgIds: companiesIds,
            buyerSearchStrings: searchStrings
          },
          update: getUpdateCacheOnChangeCompaniesIds(activeMpId),
          optimisticResponse: {
            __typename: 'Mutation',
            updateMatchingProfile: {
              __typename: 'MatchingProfile',
              ...mp,
              buyerOrgIds: companiesIds,
              buyerSearchStrings: searchStrings
            }
          }
        })
          .then(() => {
            onComplete && onComplete();
          })
          .catch(() => {
            notification.error({
              description: t('Common.unknownErrorDesc'),
              message: t('Common.unknownError')
            });
          });
    },
    [activeMpId, mp, t, updateMpCompaniesIds]
  );
  return [updateMpCompaniesIdsFn, { loading, error }];
}
export function useUpdateMpContractValue(): [
  (data: MpContractValue, onComplete?: () => void) => void,
  { loading: boolean; error: Error | undefined }
] {
  const { t } = useTranslation();
  const [activeMpId] = useActiveMpIdState();

  const [updateMpContractValue, { loading, error }] = useMutation(UPDATE_MP_CONTRACT_VALUE);

  const updateMpContractValueFn = useCallback(
    (data: MpContractValue, onComplete?: () => void) => {
      const { includeMissingValue, ...contractValueRange } = data;
      activeMpId &&
        updateMpContractValue({
          variables: { id: activeMpId, contractValueRange, includeMissingValue }
        })
          .then(() => {
            onComplete && onComplete();
          })
          .catch(() => {
            notification.error({
              description: t('Common.unknownErrorDesc'),
              message: t('Common.unknownError')
            });
          });
    },
    [activeMpId, t, updateMpContractValue]
  );
  return [updateMpContractValueFn, { loading, error }];
}

export function useUpdateMpBuyerTypes(): [
  (buyerTypes: BuyerType[], onComplete?: () => void) => void,
  { loading: boolean; error: Error | undefined }
] {
  const { t } = useTranslation();
  const [activeMpId] = useActiveMpIdState();

  const [updateMpBuyerTypes, { loading, error }] = useMutation(UPDATE_MP_BUYER_TYPES);

  const updateMpMpBuyerTypesFn = useCallback(
    (buyerTypes: BuyerType[], onComplete?: () => void) => {
      activeMpId &&
        updateMpBuyerTypes({
          variables: { id: activeMpId, buyerTypes }
        })
          .then(() => {
            onComplete && onComplete();
          })
          .catch(() => {
            notification.error({
              description: t('Common.unknownErrorDesc'),
              message: t('Common.unknownError')
            });
          });
    },
    [activeMpId, t, updateMpBuyerTypes]
  );
  return [updateMpMpBuyerTypesFn, { loading, error }];
}

export function getUpdateCacheOnChangeCompaniesIds(mpId: string): MutationUpdaterFn<IUpdateMpResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const mpRef = cache.identify({
      __typename: 'MatchingProfile',
      id: mpId
    });
    cache.modify({
      id: mpRef,
      fields: {
        buyerOrgIds() {
          return data.updateMatchingProfile.buyerOrgIds;
        },
        buyerSearchStrings() {
          return data.updateMatchingProfile.buyerSearchStrings;
        }
      }
    });
  };
}

export function useUpdateMpBTags(): MutationTuple<IUpdateMpResponse, { id: string; bTagIds: string[] }> {
  return useMutation(UPDATE_MP_BTAGS);
}
export function getUpdateCacheOnUpdateMpBTags(mpId: string): MutationUpdaterFn<IUpdateMpResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const mpRef = cache.identify({
      __typename: 'MatchingProfile',
      id: mpId
    });
    cache.modify({
      id: mpRef,
      fields: {
        bTagIds() {
          return data.updateMatchingProfile.bTagIds;
        }
      }
    });
  };
}

export function useUpdateMpCpvCodes(): MutationTuple<
  IUpdateMpResponse,
  { id: string; cpvCodes: string[]; cpvCodesExact: string[] }
> {
  return useMutation(UPDATE_MP_CPV_CODES);
}
export function getUpdateCacheOnUpdateMpCpvCodes(mpId: string): MutationUpdaterFn<IUpdateMpResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const mpRef = cache.identify({
      __typename: 'MatchingProfile',
      id: mpId
    });
    cache.modify({
      id: mpRef,
      fields: {
        cpvCodes() {
          return data.updateMatchingProfile.cpvCodes;
        },
        cpvCodesExact() {
          return data.updateMatchingProfile.cpvCodesExact;
        }
      }
    });
  };
}

export function useUpdateMpNutsCodes(): MutationTuple<
  IUpdateMpResponse,
  { id: string; nutsCodes: string[]; nutsCodesExact: string[] }
> {
  return useMutation(UPDATE_MP_NUTS_CODES);
}

export function getUpdateCacheOnUpdateMpNutsCodes(mpId: string): MutationUpdaterFn<IUpdateMpResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const mpRef = cache.identify({
      __typename: 'MatchingProfile',
      id: mpId
    });
    cache.modify({
      id: mpRef,
      fields: {
        nutsCodes() {
          return data.updateMatchingProfile.nutsCodes;
        },
        nutsCodesExact() {
          return data.updateMatchingProfile.nutsCodesExact;
        }
      }
    });
  };
}

export function useUpdateMpName(): MutationTuple<IUpdateMpResponse, { id: string; name: string }> {
  return useMutation(UPDATE_MP_NAME);
}
export function getUpdateCacheOnUpdateMpName(mpId: string): MutationUpdaterFn<IUpdateMpResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const mpRef = cache.identify({
      __typename: 'MatchingProfile',
      id: mpId
    });
    cache.modify({
      id: mpRef,
      fields: {
        name() {
          return data.updateMatchingProfile.name;
        }
      }
    });
  };
}

export interface ICreateMpResponse {
  __typename: 'Mutation';
  createMatchingProfile: IApiMp & {
    __typename: 'MatchingProfile';
  };
}

export function useCreateMp(): MutationTuple<
  ICreateMpResponse,
  {
    name: string;
    profileType?: ProfileType;
    buyerOrgIds?: string[];
    buyerSearchStrings?: string[];
    keywords?: MpNewKeyword[];
    supplierOrgIds?: SupplierOrg[];
    supplierSearchStrings?: SupplierString[];
    supplierSearchLogic?: JunctionLogic;
    nutsCodes?: string[];
    nutsCodesExact?: string[];
    cpvCodes?: string[];
    cpvCodesExact?: string[];
    buyerTypes?: BuyerType[];
    contractValue?: ContractValueRange;
    contractValueIncludeMissingValue?: boolean;
  }
> {
  return useMutation(CREATE_MP);
}

export function getUpdateCacheOnCreateMp(isExpiringProfile?: boolean): MutationUpdaterFn<ICreateMpResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }

    const newMpRef = cache.identify({ __typename: 'MatchingProfile', id: data.createMatchingProfile.id });
    newMpRef &&
      cache.modify({
        fields: {
          getAllMyMatchingProfiles: (existing: Reference[], { storeFieldName, toReference }) => {
            const includesExpProfile = storeFieldName.includes(ProfileType.ExpiringContract);
            if (isExpiringProfile ? includesExpProfile : !includesExpProfile) {
              return [...existing, toReference(newMpRef)];
            }
            return existing;
          }
        }
      });
  };
}

export function useDeleteMp(): MutationTuple<
  { deleteMatchingProfile: boolean; __typename: 'Mutation' },
  { id: string }
> {
  return useMutation(DELETE_MP);
}
export function getUpdateCacheOnDeleteMp(
  id: string,
  isExpiringProfile?: boolean
): MutationUpdaterFn<{
  deleteMatchingProfile: boolean;
  __typename: 'Mutation';
}> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }

    cache.modify({
      fields: {
        getAllMyMatchingProfiles: (existing: Reference[], { storeFieldName, readField }) => {
          const includesExpProfile = storeFieldName.includes(ProfileType.ExpiringContract);
          if (isExpiringProfile ? includesExpProfile : !includesExpProfile) {
            const newRefs = [...existing].filter(mp => readField({ fieldName: id, from: mp }) !== id);
            if (!!newRefs.length) {
              return newRefs;
            } else {
              return;
            }
          }
          return existing;
        }
      }
    });

    cache.evict({ id: cache.identify({ id, __typename: 'MatchingProfile' }) });
    cache.gc();
    !isExpiringProfile && clearTendersCacheOnMpUpdate(cache, id);
  };
}

export function useMpSnapshot(): [boolean, (variables?: ApiTendersVars) => void] {
  const qp = useQueryParams();
  const qpMpId = useMemo(
    () => (isString(qp[TendersSearchParams.matchingProfileId]) ? qp[TendersSearchParams.matchingProfileId] : undefined),
    [qp]
  );
  const { data: mp } = useLoadMp(qpMpId);
  const [refetch] = useLazyQuery<ApiTendersData, ApiTendersVars>(GET_TENDERS);
  const { cache } = useApolloClient();
  const mpValues: IMpSnapshotValues | undefined = useMemo(
    () =>
      mp
        ? {
            id: mp.id,
            nutsCodes: mp.nutsCodes,
            nutsCodesExact: mp.nutsCodesExact,
            cpvCodes: mp.cpvCodes,
            cpvCodesExact: mp.cpvCodesExact,
            bTagIds: mp.bTagIds,
            keywords: mp.keywords,
            buyerOrgIds: mp.buyerOrgIds,
            buyerSearchStrings: mp.buyerSearchStrings,
            supplierSearchTerms: mp.supplierSearchTerms,
            supplierSearchLogic: mp.supplierSearchLogic,
            contractValueCurrency: mp.contractValueCurrency,
            contractValueMax: mp.contractValueMax,
            contractValueMin: mp.contractValueMin,
            contractValueIncludeMissingValue: mp.contractValueIncludeMissingValue,
            buyerTypes: mp.buyerTypes
          }
        : undefined,
    [mp]
  );

  const [snapshot, setSnapshot] = useState<IMpSnapshot>(INIT_SNAPSHOT);

  useEffect(() => {
    setSnapshot(currentState => {
      const isProfileChanged =
        currentState.initial === undefined || (mpValues && currentState.initial.id !== mpValues.id);
      if (isProfileChanged) {
        return {
          ...currentState,
          initial: mpValues,
          isUpdated: false
        };
      }
      return {
        ...currentState,
        isUpdated: !isEqual(currentState.initial, mpValues)
      };
    });
  }, [mpValues]);
  const reset = useCallback(
    (variables?: ApiTendersVars) => {
      setSnapshot(currentState => ({
        ...currentState,
        initial: mpValues,
        isUpdated: !currentState.isUpdated
      }));
      const tendersQ: string[] = [];
      cache.modify({
        fields: {
          getTenders: (existing, { storeFieldName }) => {
            if (snapshot.initial?.id && storeFieldName.includes(snapshot.initial.id)) {
              tendersQ.push(storeFieldName);
            }
            return existing;
          }
        }
      });
      if (tendersQ.length) {
        tendersQ.forEach(queryId => {
          cache.evict({
            fieldName: queryId
          });
        });
        cache.gc();
      } else {
        refetch({ variables });
      }
    },
    [cache, mpValues, refetch, snapshot.initial]
  );

  return useMemo(() => {
    return [snapshot.isUpdated, reset];
  }, [reset, snapshot]);
}

export function usePrefixSearchLimit(): number {
  const isSearchLimit50 = useFeatureFlag(FeatureFlag.SearchLimit50);
  return useMemo(() => (isSearchLimit50 ? 50 : PREFIX_SEARCH_LIMIT), [isSearchLimit50]);
}
